import React, { useState } from "react";
import Link from "../../components/Link";
import AccountLayout from "../../account/AccountLayout";
import { useAuth } from "../../auth/AuthProvider";
import { downloadAccountExport, requestAccountDeletion } from "../../account/accountApiClient";
import { AccountButton, AccountCard, AccountStatus } from "../../account/AccountUi";
import { reauthenticateWithGoogle, formatAuthError } from "../../billing/firebaseAuth";
import { deletionIdempotencyKeyForUid, persistDeletionReceipt } from "../../account/deletionStorage";
import { navigate } from "../../lib/navigate";
import { SITE } from "../../config/site";

export default function PrivacySupportPage() {
  const auth = useAuth();
  const [exporting, setExporting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [understood, setUnderstood] = useState(false);
  const [message, setMessage] = useState(null);
  const [tone, setTone] = useState("info");

  async function onExport() {
    setExporting(true);
    setMessage(null);
    try {
      const blob = await downloadAccountExport();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "stratify-account-export.json";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setTone("info");
      setMessage("Download started. The file lists included collections, truncation, and omissions.");
    } catch (err) {
      setTone("error");
      setMessage(err?.message || "Could not export account data.");
    } finally {
      setExporting(false);
    }
  }

  async function onDelete() {
    if (confirmText.trim().toUpperCase() !== "DELETE" || !understood) {
      setTone("error");
      setMessage("Type DELETE and confirm the consequences before continuing.");
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      await reauthenticateWithGoogle();
      const idempotencyKey = deletionIdempotencyKeyForUid(auth.uid);
      const result = await requestAccountDeletion({ idempotencyKey });
      persistDeletionReceipt({
        jobId: result.jobId,
        deletionCapability: result.deletionCapability,
      });
      await auth.signOut();
      navigate("/account/deletion-status", { replace: true });
    } catch (err) {
      setTone("error");
      setMessage(formatAuthError(err) || err?.message || "Could not request deletion.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AccountLayout
      title="Privacy & support"
      description="Export your data, contact support, or close your Stratify account."
      path="/account/privacy"
    >
      <div className="space-y-5">
        <AccountCard>
          <h2 className="text-base font-semibold text-ink">Support</h2>
          <p className="mt-2 text-sm text-muted">
            Email{" "}
            <a className="font-semibold text-ink underline underline-offset-4" href={`mailto:${SITE.supportEmail}`}>
              {SITE.supportEmail}
            </a>
            . Do not email passwords or Google recovery codes.
          </p>
          <Link to="/support" className="mt-3 inline-flex min-h-11 items-center text-sm font-semibold text-ink underline underline-offset-4">
            Open support
          </Link>
        </AccountCard>

        <AccountCard>
          <h2 className="text-base font-semibold text-ink">Export your data</h2>
          <p className="mt-2 text-sm text-muted">
            Bounded JSON of your own documents. Credentials, provider secrets, and Stripe identifiers are omitted. Coverage and
            truncation are listed in the file.
          </p>
          <AccountButton className="mt-4" disabled={exporting} onClick={onExport}>
            {exporting ? "Preparing…" : "Download export"}
          </AccountButton>
        </AccountCard>

        <AccountCard>
          <h2 className="text-base font-semibold text-ink">Delete account</h2>
          <p className="mt-2 text-sm text-muted">
            This requests closure of your Stratify account, associated data, and paid access according to server policy. Apple
            subscriptions must still be managed in Apple Subscriptions. Deletion does not issue refunds. This cannot be undone
            from the website.
          </p>
          <label className="mt-4 flex items-start gap-3 text-sm text-ink/80">
            <input type="checkbox" className="mt-1 h-4 w-4" checked={understood} onChange={(e) => setUnderstood(e.target.checked)} />
            <span>I understand my account, data, and subscription access will be closed, and this does not refund past charges.</span>
          </label>
          <label className="mt-4 block text-sm font-medium text-ink">
            Type DELETE to confirm
            <input
              value={confirmText}
              onChange={(event) => setConfirmText(event.target.value)}
              className="mt-1 w-full rounded-xl border border-ink/10 px-3 py-2.5 text-sm"
            />
          </label>
          <AccountButton variant="danger" className="mt-4" disabled={busy} onClick={onDelete}>
            {busy ? "Working…" : "Reauthenticate and delete"}
          </AccountButton>
        </AccountCard>

        {message ? <AccountStatus tone={tone}>{message}</AccountStatus> : null}
      </div>
    </AccountLayout>
  );
}
